import type { Schema } from '@tix/openapi-sdk'

import type { GA4DonationEcommercePayload, GA4DonationPurchasePayload, GA4DonationViewPayload } from './donationGa4'
import { getEnv } from './environment'

interface GA4ItemBase {
  /**
   * add_to_cart/remove_from_cart: Resolver ID.
   * view_item: Flow Definition ID.
   */
  item_id: string
  /**
   * add_to_cart/remove_from_cart: prompt summary title.
   * view_item: Flow Definition name.
   */
  item_name: string
  /**
   * add_to_cart/remove_from_cart: variant name / session id / date / pick_n option.
   * view_item: 'bundle'.
   */
  item_variant: string | undefined
  /** Seller ID. */
  location_id: string
  /** Hardcoded — the flows API doesn't expose per-item pricing yet. */
  price: '0.00'
}

export interface GA4ViewItem extends GA4ItemBase {
  quantity: 1
}

export interface GA4CartItem extends GA4ItemBase {
  /** Flow Definition ID. */
  item_list_id: string
  /** Flow Definition name. */
  item_list_name: string
  /** 0-based step index within the flow. */
  index: number
  /** Set for `pick_variants` (count per variant); omitted for other step types. */
  quantity?: number
}

interface GA4FlowEcommercePayload<TItems extends readonly GA4ItemBase[]> {
  currency: string
  /** Hardcoded — see `GA4ItemBase.price`. */
  value: '0.00'
  items: TItems
}

export interface GA4EventPayloadMap {
  view_item: GA4FlowEcommercePayload<[GA4ViewItem]> | GA4DonationViewPayload
  add_to_cart: GA4FlowEcommercePayload<GA4CartItem[]> | GA4DonationEcommercePayload
  remove_from_cart: GA4FlowEcommercePayload<GA4CartItem[]>
  begin_checkout: GA4DonationEcommercePayload
  purchase: GA4DonationPurchasePayload
}

/** Flow step types that emit a `bundle_step` event. See `useBundleStepTracking`. */
export type BundleStepType = Extract<
  Schema<'action_input'>['type'],
  'pick_n' | 'pick_variants' | 'pick_date' | 'pick_time' | 'survey'
>

/** Non-ecommerce GA4 event — one user interaction within a flow step. */
export interface GA4BundleStep {
  bundle_id: string
  bundle_name: string
  /** Flat interaction sequence across the whole flow — see `provideBundleStepContext`. */
  bundle_step_number: number
  bundle_step_type: BundleStepType
  /** Resulting state after the interaction — see `flowStepValue.ts`. */
  bundle_step_value: string
}

export interface GA4CustomEventPayloadMap {
  bundle_step: GA4BundleStep
}

/**
 * Push a GA4 ecommerce event to the GTM `window.dataLayer`, then immediately
 * push `{ ecommerce: null }` to clear the ecommerce object.
 *
 * GA4 docs say "clear before". We deliberately clear AFTER instead, matching
 * the long-standing pattern in `apps/web-client/src/checkout/helpers/ga4.ts`,
 * because GTM's dataLayer is sticky.
 *
 * @see apps/web-client/src/checkout/helpers/ga4.ts — same pattern
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clearing_the_ecommerce_object
 */
export function triggerGA4Event<E extends keyof GA4EventPayloadMap>(name: E, payload: GA4EventPayloadMap[E]): void {
  if (!isGA4()) return
  window.dataLayer.push({ event: name, ecommerce: payload })
  window.dataLayer.push({ ecommerce: null })
}

/** Flat payload — no `ecommerce` wrapper, so no clear push. */
export function triggerGA4CustomEvent<E extends keyof GA4CustomEventPayloadMap>(
  name: E,
  payload: GA4CustomEventPayloadMap[E],
): void {
  if (!isGA4()) return
  window.dataLayer.push({ event: name, ...payload })
}

/**
 * Similar to `apps/web-client/src/checkout/helpers/ga4.ts` `isGA4()`, but also
 * checks that `window.dataLayer` is an array. This is to prevent the event
 * from being pushed to a never-consumed array in Storybook.
 */
function isGA4(): boolean {
  const env = getEnv()
  return Boolean(env?.config?.web?.gtm_container_id && Array.isArray(window.dataLayer))
}
